import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  Rocket, Loader2, Play, Pause, RefreshCw, CheckCircle2, XCircle, ShieldAlert,
  TrendingUp, Lightbulb, Swords, DollarSign, Users, Eye, FlaskConical, ListChecks,
  ShieldCheck, Sparkles, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SocialPerformancePanel = ({ headers }) => {
  const [perf, setPerf] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    axios.get(`${API}/social/analytics/summary?account_scope=system`, { headers })
      .then(r => { setPerf(r.data); setLoaded(true); })
      .catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!loaded) return null;
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid="social-performance-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <h3 className="text-sm font-bold text-white">Social Performance</h3>
        {perf?.has_real_data ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                data-testid="social-perf-real-badge">REAL PLATFORM DATA</span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700"
                data-testid="social-perf-no-data-badge">NO REAL DATA YET — connect accounts &amp; publish</span>
        )}
      </div>
      {perf?.has_real_data ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Posts Measured" value={perf.summary.posts_measured} />
          <Metric label="Avg Engagement" value={perf.summary.avg_engagement_rate != null ? `${(perf.summary.avg_engagement_rate * 100).toFixed(1)}%` : '—'} />
          <Metric label="Platforms" value={Object.keys(perf.summary.platforms || {}).length} />
          <Metric label="Top Post" value={perf.summary.top_post ? perf.summary.top_post.title?.slice(0, 18) : '—'} />
        </div>
      ) : (
        <p className="text-xs text-zinc-500">
          All other numbers on this page are internal platform counts; agent suggestions are LLM recommendations.
          Once Delphi's social accounts are connected and posts go live, real per-post metrics appear here.
        </p>
      )}
    </div>
  );
};

const Metric = ({ label, value }) => (  <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
    <p className="text-lg font-bold text-white">{value}</p>
    <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</p>
  </div>
);

const Section = ({ icon: Icon, title, children, testid, accent }) => (
  <div className={`bg-zinc-900/60 border rounded-2xl p-5 ${accent ? 'border-orange-500/20' : 'border-zinc-800'}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-orange-400" />
      <h2 className="text-xs font-semibold uppercase tracking-wider text-zinc-400">{title}</h2>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <p className="text-xs text-zinc-600 py-2">{text}</p>;

export default function GrowthCommandCenterPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/autonomy/growth/command-center`, { headers });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    refresh();
    const iv = setInterval(refresh, 20000);
    return () => clearInterval(iv);
  }, [refresh]);

  if (forbidden) {
    return (
      <AppShell>
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center px-6" data-testid="growth-forbidden">
          <div className="max-w-md text-center space-y-3">
            <Rocket className="w-10 h-10 text-orange-400 mx-auto" />
            <h1 className="text-xl font-bold text-white">Founder access required</h1>
            <p className="text-sm text-zinc-400">
              The Growth Command Center controls Delphi's own self-operating business
              and is restricted to the founder account. Your personal autonomy tools
              are available in the Autonomy Center and Autonomous Delphi hub.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  const act = async (label, fn) => {
    setBusy(label);
    try { await fn(); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
    finally { setBusy(null); }
  };

  const toggleMode = () => act('mode', async () => {
    const verb = data.self_operating_enabled ? 'disable' : 'enable';
    await axios.post(`${API}/autonomy/growth/${verb}`, {}, { headers });
    toast.success(verb === 'enable' ? 'Self-Operating Mode ENABLED — daily growth cycles active' : 'Self-Operating Mode disabled');
  });

  const togglePause = () => act('pause', async () => {
    const verb = data.system_paused ? 'resume' : 'pause';
    await axios.post(`${API}/autonomy/growth/${verb}`, {}, { headers });
    setData(d => ({ ...d, system_paused: verb === 'pause' }));
    toast.success(verb === 'pause' ? 'Growth org PAUSED' : 'Growth org resumed');
  });

  const runCycle = () => act('cycle', async () => {
    await axios.post(`${API}/autonomy/growth/cycle/run`, {}, { headers });
    toast.success('Growth cycle started — Delphi is growing Delphi…');
  });

  const generateTasks = () => act('gen', async () => {
    const { data: d } = await axios.post(`${API}/autonomy/growth/generate-tasks`, {}, { headers });
    toast.success(`Delphi generated ${d.tasks_created} marketing tasks`);
  });

  const execTask = (id) => act(id, async () => {
    const { data: t } = await axios.post(`${API}/autonomy/growth/tasks/${id}/execute`, {}, { headers });
    toast.success(t.status === 'completed' ? 'Task executed' : `Task ${t.status}`);
  });

  const dismissTask = (id) => act(`d-${id}`, async () => {
    await axios.post(`${API}/autonomy/growth/tasks/${id}/dismiss`, {}, { headers });
    toast.success('Dismissed');
  });

  if (!data) {
    return (
      <AppShell>
        <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="growth-loading">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      </AppShell>
    );
  }

  const m = data.metrics;
  const cycleRunning = (data.cycles || []).some(c => c.status === 'running');

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-16" data-testid="growth-command-center">
        {/* Header */}
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Rocket className="w-6 h-6 text-orange-400" />
              <div>
                <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="growth-title">Growth Command Center</h1>
                <p className="text-sm text-white/40">Delphi uses Delphi to grow Delphi — self-operating business mode</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={toggleMode} disabled={busy === 'mode'} data-testid="self-operating-toggle"
                className={data.self_operating_enabled ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-zinc-700 hover:bg-zinc-600'}>
                {busy === 'mode' ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  <>{data.self_operating_enabled ? 'Self-Operating: ON' : 'Self-Operating: OFF'}</>}
              </Button>
              <Button onClick={runCycle} disabled={busy === 'cycle' || cycleRunning} data-testid="run-cycle-btn"
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
                {cycleRunning ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cycle Running…</> : <><Play className="w-4 h-4 mr-2" />Run Growth Cycle</>}
              </Button>
              <Button onClick={togglePause} data-testid="growth-pause-btn"
                className={data.system_paused ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600/80 hover:bg-red-700'}>
                {data.system_paused ? <><Play className="w-4 h-4 mr-1" />Resume</> : <><Pause className="w-4 h-4 mr-1" />Pause</>}
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Metrics: conversion / revenue / awareness / campaigns */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="growth-metrics">
            <Metric label="Total Users" value={m.conversion_performance.users_total} />
            <Metric label="New (7d)" value={m.conversion_performance.users_new_7d} />
            <Metric label="Paid Subs" value={m.conversion_performance.paid_subscriptions} />
            <Metric label="Conversion" value={`${Math.round(m.conversion_performance.trial_to_paid_ratio * 100)}%`} />
            <Metric label="Share Views" value={m.awareness.share_page_views} />
            <Metric label="Public Shares" value={m.awareness.public_shares} />
            <Metric label="Assets (7d)" value={m.awareness.content_created_7d} />
            <Metric label="Campaigns" value={m.campaign_performance.campaign_creatives} />
          </div>

          {/* Social performance — REAL platform data (Phase 4) */}
          <SocialPerformancePanel headers={headers} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Today's priorities */}
            <Section icon={ListChecks} title={`Today's Priorities (${data.todays_priorities.length})`} testid="todays-priorities" accent>
              <div className="mb-3">
                <Button size="sm" onClick={generateTasks} disabled={busy === 'gen'} data-testid="generate-tasks-btn"
                  className="bg-white/[0.08] hover:bg-white/[0.15] text-white h-8 text-xs">
                  {busy === 'gen' ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                  Generate Today's Tasks
                </Button>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {data.todays_priorities.length === 0 && <Empty text="No priorities yet — generate today's tasks or run a growth cycle." />}
                {data.todays_priorities.map(t => (
                  <div key={t.id} className="p-2.5 bg-zinc-800/40 rounded-lg" data-testid={`priority-${t.id}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold uppercase ${t.status === 'awaiting_approval' ? 'bg-amber-500/10 text-amber-400' : 'bg-sky-500/10 text-sky-400'}`}>
                        {t.status === 'awaiting_approval' ? 'needs approval' : 'ready'}
                      </span>
                      <p className="text-xs text-white flex-1 min-w-0 truncate">{t.title}</p>
                      <button onClick={() => execTask(t.id)} disabled={busy === t.id} title="Execute"
                        className="text-emerald-400 hover:text-emerald-300" data-testid={`exec-priority-${t.id}`}>
                        {busy === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      </button>
                      <button onClick={() => dismissTask(t.id)} title="Dismiss"
                        className="text-zinc-600 hover:text-red-400" data-testid={`dismiss-priority-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <p className="text-[10px] text-zinc-600 mt-0.5">{t.category} · conf {Math.round((t.confidence_score || 0) * 100)}%</p>
                  </div>
                ))}
              </div>
            </Section>

            {/* Active experiments + recommended actions */}
            <div className="space-y-5">
              <Section icon={FlaskConical} title={`Active Experiments (${data.active_experiments.length})`} testid="active-experiments">
                {data.active_experiments.length === 0 && <Empty text="No experiments running." />}
                {data.active_experiments.map(e => (
                  <div key={e.id} className="flex items-center gap-2 py-1.5 text-xs">
                    <Loader2 className="w-3 h-3 animate-spin text-orange-400" />
                    <span className="text-white truncate flex-1">{e.objective}</span>
                    <span className="text-zinc-600">{e.status}</span>
                  </div>
                ))}
              </Section>
              <Section icon={Lightbulb} title="Recommended Actions" testid="recommended-actions">
                {(data.recommended_actions || []).length === 0 && <Empty text="Run a growth cycle to get next-cycle recommendations." />}
                {(data.recommended_actions || []).map((r, i) => (
                  <div key={r.task_id || i} className="flex items-center gap-2 py-1.5 text-xs">
                    <Sparkles className="w-3 h-3 text-orange-400 shrink-0" />
                    <span className="text-zinc-300 truncate flex-1">{r.brief}</span>
                    <span className="text-[9px] text-zinc-600">{r.agent_id}</span>
                  </div>
                ))}
              </Section>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <Section icon={TrendingUp} title="Growth Opportunities" testid="growth-opportunities">
              {(data.growth_opportunities || []).length === 0 && <Empty text="Opportunities appear after research/analytics agents run." />}
              {(data.growth_opportunities || []).map((o, i) => (
                <div key={i} className="py-1.5 text-xs border-b border-zinc-800/50 last:border-0">
                  <p className="text-zinc-300">{o.summary}</p>
                  <p className="text-[9px] text-zinc-600 mt-0.5">{o.agent} · conf {Math.round((o.confidence || 0) * 100)}%</p>
                </div>
              ))}
            </Section>
            <Section icon={Swords} title="Competitor Signals" testid="competitor-signals">
              <div className="flex flex-wrap gap-1 mb-2">
                {data.org.competitors.map(c => <span key={c} className="px-1.5 py-0.5 text-[9px] bg-zinc-800 text-zinc-400 rounded">{c}</span>)}
              </div>
              {(data.competitor_signals || []).length === 0 && <Empty text="No competitor signals detected yet." />}
              {(data.competitor_signals || []).map((s, i) => (
                <p key={i} className="text-xs text-zinc-300 py-1">{s.summary}</p>
              ))}
            </Section>
            <Section icon={DollarSign} title="Revenue Signals" testid="revenue-signals">
              <p className="text-xs text-zinc-400 mb-2">North star: <span className="text-orange-400 font-semibold">{m.revenue_signals.north_star}</span></p>
              {Object.entries(m.revenue_signals.tier_breakdown).map(([tier, n]) => (
                <div key={tier} className="flex justify-between text-xs py-0.5">
                  <span className="text-zinc-500 capitalize">{tier}</span>
                  <span className="text-white font-semibold">{n}</span>
                </div>
              ))}
            </Section>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Awaiting approval */}
            <Section icon={ShieldAlert} title={`Actions Awaiting Approval (${data.actions_awaiting_approval.length})`} testid="awaiting-approval">
              {data.actions_awaiting_approval.length === 0 && <Empty text="Nothing awaiting approval." />}
              {data.actions_awaiting_approval.map(ap => (
                <div key={ap.id} className="flex items-center gap-2 py-1.5 text-xs">
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 font-bold uppercase">{ap.risk}</span>
                  <span className="text-white truncate flex-1">{ap.title}</span>
                  <button onClick={() => execTask(ap.task_id)} className="text-emerald-400 hover:text-emerald-300" data-testid={`approve-growth-${ap.id}`}>
                    <CheckCircle2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => dismissTask(ap.task_id)} className="text-red-400/70 hover:text-red-400" data-testid={`reject-growth-${ap.id}`}>
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </Section>

            {/* Autonomous actions audit */}
            <Section icon={Eye} title="Autonomous Actions (Audit Trail)" testid="autonomous-actions">
              {data.autonomous_actions.length === 0 && <Empty text="No self-operated actions yet." />}
              <div className="max-h-56 overflow-y-auto">
                {data.autonomous_actions.map(a => (
                  <div key={a.id} className="flex items-center gap-2 py-1.5 text-[11px] border-b border-zinc-800/40 last:border-0">
                    {a.status === 'completed' ? <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" /> : <XCircle className="w-3 h-3 text-red-400 shrink-0" />}
                    <span className="text-zinc-500 shrink-0">{new Date(a.timestamp).toLocaleString()}</span>
                    <span className="text-white">{a.action_type}</span>
                    <span className="text-zinc-600 truncate flex-1">{a.agent_id} · {a.authorized_by}</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* Recent results + latest cycle */}
          <Section icon={Users} title="Recent Results & Latest Growth Cycle" testid="recent-results">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {data.recent_results.length === 0 && <Empty text="No completed growth tasks yet." />}
                {data.recent_results.map(t => (
                  <details key={t.id} className="py-1.5 border-b border-zinc-800/40 last:border-0">
                    <summary className="text-xs text-white cursor-pointer flex items-center gap-2">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" />{t.title}
                      {t.evaluation && <span className="text-[9px] text-zinc-600 ml-auto">score {(t.evaluation.score * 10).toFixed(1)}/10</span>}
                    </summary>
                    {t.actual_result?.content && (
                      <pre className="text-[10px] text-zinc-400 whitespace-pre-wrap mt-1 max-h-40 overflow-y-auto bg-black/30 rounded p-2">{t.actual_result.content}</pre>
                    )}
                  </details>
                ))}
              </div>
              <div>
                {data.latest_cycle ? (
                  <div className="text-xs space-y-2" data-testid="latest-cycle">
                    <div className="flex items-center gap-2">
                      <RefreshCw className="w-3 h-3 text-orange-400" />
                      <span className="text-white font-semibold">Cycle {data.latest_cycle.status}</span>
                      <span className="text-zinc-600">{new Date(data.latest_cycle.started_at).toLocaleString()}</span>
                    </div>
                    {data.latest_cycle.learnings && (
                      <pre className="text-[10px] text-zinc-400 whitespace-pre-wrap max-h-48 overflow-y-auto bg-black/30 rounded p-2">{data.latest_cycle.learnings}</pre>
                    )}
                  </div>
                ) : <Empty text="No growth cycles run yet." />}
              </div>
            </div>
          </Section>

          {/* Compliance */}
          <Section icon={ShieldCheck} title="Safety & Compliance" testid="compliance-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {data.compliance_policy.map((p, i) => (
                <div key={i} className="flex items-start gap-2 py-1 text-[11px] text-zinc-400">
                  <ShieldCheck className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />{p}
                </div>
              ))}
            </div>
            <p className="text-[10px] text-zinc-600 mt-3">
              Hard-blocked from autonomous execution: {data.hard_blocked_categories.join(', ')} — these always require your explicit approval.
            </p>
          </Section>
        </div>
      </div>
    </AppShell>
  );
}
